import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';

const PAGE_SIZE = 25;

// Same DataTableCard + server page/total pattern as the admin lists
// (BACKEND.md §1) — GET /orders?userId=&page=&pageSize= already scopes to
// the caller's own orders.
const useMyOrdersPage = () => {
  const { t, i18n } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');

  const [userId] = useState(() => localStorage.getItem('userId') || '');

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [detailOrder, setDetailOrder] = useState(null);

  const fetchOrders = useCallback((reset = true) => {
    if (!config?.backendUrl || !userId) return;
    if (reset) setStatus('loading'); else setLoadingMore(true);
    const page = reset ? 1 : Math.floor(rowsRef.current.length / PAGE_SIZE) + 1;
    const params = { userId, page, pageSize: PAGE_SIZE, sort };
    if (statusFilter !== 'all') params.status = statusFilter;
    axios.get(`${config.backendUrl}/orders`, { params })
      .then((res) => {
        setRows((prev) => (reset ? res.data.items : [...prev, ...res.data.items]));
        setTotal(res.data.total);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching orders', error);
        if (error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /orders · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setLoadingMore(false));
  }, [config, userId, statusFilter, sort, t]);

  useEffect(() => { fetchOrders(true); }, [fetchOrders]);

  const loadMore = () => fetchOrders(false);
  const remaining = Math.max(0, total - rows.length);
  const nextBatchSize = Math.min(PAGE_SIZE, remaining);

  const isPreviewEmptyKind = previewState === 'empty';
  const effectiveStatus = isPreviewEmptyKind ? 'ready' : (previewState || status);
  const effectiveRows = isPreviewEmptyKind ? [] : (effectiveStatus === 'ready' ? rows : []);

  return {
    t, i18n, effectiveStatus, effectiveRows, total, errorInfo,
    statusFilter, setStatusFilter, sort, setSort,
    loadingMore, loadMore, remaining, nextBatchSize,
    detailOrder, setDetailOrder,
    fetchOrders,
  };
};

export default useMyOrdersPage;
